import { RStoreSchema } from '#imports';
import { Collection, CollectionMeta } from './collection';
import { SingleConstraint, SingleConstraintField } from './constraint';
import { DataSource } from './data-source';
import { Index, IndexField } from './indexes';
import { Namespace } from './namespace';
import { PrimitiveField, PrimitiveFieldMeta } from './primitive-field';
import { SingleRelationField, SingleRelationFieldMeta } from './relation-field';

// Collection relations
export const CollectionRelations = RStoreSchema.defineRelations(Collection, ({ collection }) => ({
	source: {
		to: collection(DataSource, {
			on: {
				'MonospaceDataSource.id': 'MonospaceCollection.sourceId',
			},
		}),
	},
	namespace: {
		to: collection(Namespace, {
			on: {
				'MonospaceNamespace.id': 'MonospaceCollection.namespaceId',
			},
		}),
	},
	primitiveFields: {
		to: collection(PrimitiveField, {
			on: {
				'MonospacePrimitiveField.collectionId': 'MonospaceCollection.id',
			},
		}),
		many: true,
	},
	relationFields: {
		to: collection(SingleRelationField, {
			on: {
				'MonospaceSingleRelationField.collectionId': 'MonospaceCollection.id',
			},
		}),
		many: true,
	},
	indexes: {
		to: collection(Index, {
			on: {
				'MonospaceIndex.collectionId': 'MonospaceCollection.id',
			},
		}),
		many: true,
	},
	meta: {
		to: collection(CollectionMeta, {
			on: {
				'MonospaceCollectionMeta.collectionId': 'MonospaceCollection.id',
			},
		}),
	},
}));

export const CollectionMetaRelations = RStoreSchema.defineRelations(CollectionMeta, ({ collection }) => ({
	collection: {
		to: collection(Collection, {
			on: {
				'MonospaceCollection.id': 'MonospaceCollectionMeta.collectionId',
			},
		}),
	},
}));

// DataSource relations
export const DataSourceRelations = RStoreSchema.defineRelations(DataSource, ({ collection }) => ({
	collections: {
		to: collection(Collection, {
			on: {
				'MonospaceCollection.sourceId': 'MonospaceDataSource.id',
			},
		}),
		many: true,
	},
	namespaces: {
		to: collection(Namespace, {
			on: {
				'MonospaceNamespace.sourceId': 'MonospaceDataSource.id',
			},
		}),
		many: true,
	},
}));

// Namespace relations
export const NamespaceRelations = RStoreSchema.defineRelations(Namespace, ({ collection }) => ({
	source: {
		to: collection(DataSource, {
			on: {
				'MonospaceDataSource.id': 'MonospaceNamespace.sourceId',
			},
		}),
	},
	collections: {
		to: collection(Collection, {
			on: {
				'MonospaceCollection.namespaceId': 'MonospaceNamespace.id',
			},
		}),
		many: true,
	},
}));

// PrimitiveField relations
export const PrimitiveFieldRelations = RStoreSchema.defineRelations(PrimitiveField, ({ collection }) => ({
	collection: {
		to: collection(Collection, {
			on: {
				'MonospaceCollection.id': 'MonospacePrimitiveField.collectionId',
			},
		}),
	},
	indexedBy: {
		to: collection(IndexField, {
			on: {
				'MonospaceIndexField.fieldId': 'MonospacePrimitiveField.id',
			},
		}),
		many: true,
	},
	constrainedBy: {
		to: collection(SingleConstraintField, {
			on: {
				'MonospaceSingleConstraintField.constrainedFieldId': 'MonospacePrimitiveField.id',
			},
		}),
		many: true,
	},
	referencedBy: {
		to: collection(SingleConstraintField, {
			on: {
				'MonospaceSingleConstraintField.referencedFieldId': 'MonospacePrimitiveField.id',
			},
		}),
		many: true,
	},
	meta: {
		to: collection(PrimitiveFieldMeta, {
			on: {
				'MonospacePrimitiveFieldMeta.fieldId': 'MonospacePrimitiveField.id',
			},
		}),
	},
}));

export const PrimitiveFieldMetaRelations = RStoreSchema.defineRelations(PrimitiveFieldMeta, ({ collection }) => ({
	field: {
		to: collection(PrimitiveField, {
			on: {
				'MonospacePrimitiveField.id': 'MonospacePrimitiveFieldMeta.fieldId',
			},
		}),
	},
}));

// SingleRelationField relations
export const SingleRelationFieldRelations = RStoreSchema.defineRelations(SingleRelationField, ({ collection }) => ({
	collection: {
		to: collection(Collection, {
			on: {
				'MonospaceCollection.id': 'MonospaceSingleRelationField.collectionId',
			},
		}),
	},
	oppositeCollection: {
		to: collection(Collection, {
			on: {
				'MonospaceCollection.id': 'MonospaceSingleRelationField.oppositeCollectionId',
			},
		}),
	},
	oppositeRelationField: {
		to: collection(SingleRelationField, {
			on: {
				'MonospaceSingleRelationField.id': 'MonospaceSingleRelationField.oppositeRelationFieldId',
			},
		}),
	},
	self: {
		to: collection(SingleRelationField, {
			on: {
				'MonospaceSingleRelationField.oppositeRelationFieldId': 'MonospaceSingleRelationField.id',
			},
		}),
	},
	constraint: {
		to: collection(SingleConstraint, {
			on: {
				'MonospaceSingleConstraint.id': 'MonospaceSingleRelationField.constraintId',
			},
		}),
	},
	meta: {
		to: collection(SingleRelationFieldMeta, {
			on: {
				'MonospaceSingleRelationFieldMeta.fieldId': 'MonospaceSingleRelationField.id',
			},
		}),
	},
}));

export const SingleRelationFieldMetaRelations = RStoreSchema.defineRelations(SingleRelationFieldMeta, ({ collection }) => ({
	field: {
		to: collection(SingleRelationField, {
			on: {
				'MonospaceSingleRelationField.id': 'MonospaceSingleRelationFieldMeta.fieldId',
			},
		}),
	},
	oppositeJunctionRelationFieldMeta: {
		to: collection(SingleRelationFieldMeta, {
			on: {
				'MonospaceSingleRelationFieldMeta.fieldId': 'MonospaceSingleRelationFieldMeta.oppositeJunctionRelationFieldId',
			},
		}),
	},
}));

// Index relations
export const IndexFieldRelations = RStoreSchema.defineRelations(IndexField, ({ collection }) => ({
	index: {
		to: collection(Index, {
			on: {
				'MonospaceIndex.id': 'MonospaceIndexField.indexId',
			},
		}),
	},
	field: {
		to: collection(PrimitiveField, {
			on: {
				'MonospacePrimitiveField.id': 'MonospaceIndexField.fieldId',
			},
		}),
	},
}));

export const IndexRelations = RStoreSchema.defineRelations(Index, ({ collection }) => ({
	collection: {
		to: collection(Collection, {
			on: {
				'MonospaceCollection.id': 'MonospaceIndex.collectionId',
			},
		}),
	},
	fields: {
		to: collection(IndexField, {
			on: {
				'MonospaceIndexField.indexId': 'MonospaceIndex.id',
			},
		}),
		many: true,
	},
}));

// Constraint relations
export const SingleConstraintFieldRelations = RStoreSchema.defineRelations(SingleConstraintField, ({ collection }) => ({
	constraint: {
		to: collection(SingleConstraint, {
			on: {
				'MonospaceSingleConstraint.id': 'MonospaceSingleConstraintField.constraintId',
			},
		}),
	},
	constrainedField: {
		to: collection(PrimitiveField, {
			on: {
				'MonospacePrimitiveField.id': 'MonospaceSingleConstraintField.constrainedFieldId',
			},
		}),
	},
	referencedField: {
		to: collection(PrimitiveField, {
			on: {
				'MonospacePrimitiveField.id': 'MonospaceSingleConstraintField.referencedFieldId',
			},
		}),
	},
}));

export const SingleConstraintRelations = RStoreSchema.defineRelations(SingleConstraint, ({ collection }) => ({
	constrainedRelation: {
		to: collection(SingleRelationField, {
			on: {
				'MonospaceSingleRelationField.constraintId': 'MonospaceSingleConstraint.id',
			},
		}),
	},
	fieldMappings: {
		to: collection(SingleConstraintField, {
			on: {
				'MonospaceSingleConstraintField.constraintId': 'MonospaceSingleConstraint.id',
			},
		}),
		many: true,
	},
}));
